/**
 * Resolve o Patient da rota Gerenciar: state da navegação ou consentimento.
 * @module useEcommerceManagePatient
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { Patient } from "fhir/r5";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import { ecommercePatientsService } from "../services/ecommercePatientsService";
import { matchesEcommercePatientKey } from "../helpers/ecommerceManageRoutes";

interface UseEcommerceManagePatientParams {
  patientKey: string;
  cnpj?: string;
  patientFromState?: Patient | null;
}

export function useEcommerceManagePatient({
  patientKey,
  cnpj,
  patientFromState,
}: UseEcommerceManagePatientParams) {
  const statePatient =
    patientFromState &&
    patientKey &&
    matchesEcommercePatientKey(patientFromState, patientKey)
      ? patientFromState
      : null;

  const resolveTask = useCallback(
    (organizationCnpj: string, key: string) =>
      ecommercePatientsService.resolveConsentPatientByKey(
        organizationCnpj,
        key,
      ),
    [],
  );

  const {
    run: runResolve,
    loading,
    data,
    error,
    reset,
  } = useAsyncTask<[string, string], Patient | null>({
    task: resolveTask,
  });

  const [hasAttempted, setHasAttempted] = useState(Boolean(statePatient));
  const generationRef = useRef(0);

  useEffect(() => {
    if (statePatient || !patientKey || !cnpj) {
      generationRef.current += 1;
      reset();
      setHasAttempted(Boolean(statePatient) || !patientKey);
      return;
    }

    const generation = ++generationRef.current;
    setHasAttempted(false);
    void runResolve(cnpj, patientKey).finally(() => {
      if (generation === generationRef.current) {
        setHasAttempted(true);
      }
    });

    return () => {
      generationRef.current += 1;
    };
  }, [statePatient, patientKey, cnpj, runResolve, reset]);

  const patient = statePatient ?? data ?? null;
  const isLoading =
    !statePatient && Boolean(patientKey && cnpj) && (!hasAttempted || loading);
  const isMissing =
    !patientKey || (hasAttempted && !isLoading && !patient) || Boolean(error);

  return {
    patient,
    isLoading,
    isMissing: isMissing && !isLoading,
  };
}
